import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Check, Crown, Loader2, RotateCcw } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { useAuthUser } from "@/hooks/useAuthUser";
import { APPLE_PRODUCT_IDS, RTStoreKit, iapAvailable, type AppleProduct } from "@/lib/native-iap";
import {
  getAppleEligibility,
  recordAppleDigitalConsent,
  syncApplePurchases,
} from "@/lib/apple-iap.functions";
import { DigitalConsentCheckbox } from "@/components/DigitalConsentCheckbox";
import { DIGITAL_CONSENT_VERSION } from "@/lib/digital-consent";
import { trackFunnel, trackCheckoutFailed } from "@/lib/funnel";

export const Route = createFileRoute("/app-membership")({
  validateSearch: (s) => z.object({ preview: z.coerce.number().optional(), plan: z.string().optional() }).parse(s),
  head: () => ({
    meta: [
      { title: "Membership plans in the app | Recovery Trauma™" },
      {
        name: "description",
        content: "Choose a Recovery Trauma™ membership inside the iPhone app, billed through Apple.",
      },
      { property: "og:title", content: "Membership plans in the app | Recovery Trauma™" },
      {
        property: "og:description",
        content: "Monthly, 6-month and yearly Recovery Trauma™ memberships through the App Store.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AppMembershipPage,
});

/** Shown until Apple's own localised prices arrive (and in the review preview). */
const FALLBACK: Record<string, { name: string; price: string; per: string; note?: string }> = {
  "com.recoverytrauma.app.membership.monthly": { name: "Monthly", price: "$14.99", per: "per month" },
  "com.recoverytrauma.app.membership.6month": {
    name: "6-Month",
    price: "$59.99",
    per: "every 6 months",
    note: "Save 33%",
  },
  "com.recoverytrauma.app.membership.yearly": {
    name: "Yearly",
    price: "$89.99",
    per: "per year",
    note: "Best value · save 50%",
  },
};

const INCLUDED = [
  "Every healing journey, including Breaking Free",
  "Guided audio: sound baths, breathing and EFT tapping",
  "Private journals with optional voice notes",
  "Self-care calendar, learning library and live events",
];

function AppMembershipPage() {
  const { preview, plan: planParam } = Route.useSearch();
  const navigate = useNavigate();
  const user = useAuthUser();
  const [consent, setConsent] = useState(false);
  const recordConsent = useServerFn(recordAppleDigitalConsent);
  const [ready, setReady] = useState(false);
  const [native, setNative] = useState(false);
  const [products, setProducts] = useState<AppleProduct[]>([]);
  const [selected, setSelected] = useState<string>(
    planParam && (APPLE_PRODUCT_IDS as readonly string[]).includes(planParam) ? planParam : APPLE_PRODUCT_IDS[2],
  );
  const [busy, setBusy] = useState<null | "buy" | "restore">(null);
  const [message, setMessage] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const [eligibility, setEligibility] = useState<{ isMember: boolean; provider: string | null } | null>(null);

  const sync = useServerFn(syncApplePurchases);
  const eligible = useServerFn(getAppleEligibility);

  useEffect(() => {
    const n = iapAvailable();
    setNative(n);
    setReady(true);
    if (!n && !preview) navigate({ to: "/choose-membership", replace: true });
    if (n) {
      RTStoreKit.getProducts({ productIds: [...APPLE_PRODUCT_IDS] })
        .then((r) => setProducts(r.products))
        .catch(() => setMessage({ tone: "err", text: "Couldn't reach the App Store. Please try again." }));
    }
  }, [preview, navigate]);

  useEffect(() => {
    if (!ready) return;
    trackFunnel(
      "page_view",
      { source: "app_membership", plan: selected, dedupeKey: "app_membership_view" },
      { surface: "apple_payment_page" },
    );
  }, [ready, selected]);

  useEffect(() => {
    if (!user || !native) return;
    eligible().then(setEligibility).catch(() => {});
  }, [user, native, eligible]);

  const plans = useMemo(
    () =>
      APPLE_PRODUCT_IDS.map((id) => {
        const p = products.find((x) => x.id === id);
        const f = FALLBACK[id];
        return { id, name: f.name, price: p?.displayPrice ?? f.price, per: f.per, note: f.note };
      }),
    [products],
  );

  const finish = useCallback(
    async (ids: string[]) => {
      const r = await sync({ data: { transactionIds: ids } });
      if (!r.ok) throw new Error(r.error);
      window.dispatchEvent(new Event("rt-membership-refresh"));
      if (r.active) {
        trackFunnel("membership_activated", {
          source: "app_membership",
          provider: "apple",
          plan: selected,
          dedupeKey: `apple-active:${ids[0] ?? selected}`,
        });
        setMessage({ tone: "ok", text: `Welcome — your ${r.plan} membership is active.` });
        setTimeout(() => navigate({ to: "/dashboard" }), 1200);
      } else {
        setMessage({ tone: "err", text: "We found your Apple subscription, but it has ended." });
      }
    },
    [sync, navigate],
  );

  const buy = async () => {
    if (!native || !user) return;
    // Never sell a second subscription: wait for the membership check first.
    if (!eligibility || eligibility.isMember) return;
    if (!consent) {
      setMessage({ tone: "err", text: "Please tick the consent box before continuing." });
      return;
    }
    setBusy("buy");
    setMessage(null);
    trackFunnel("payment_method_selected", {
      source: "app_membership",
      provider: "apple",
      plan: selected,
    });
    trackFunnel("payment_started", {
      source: "app_membership",
      provider: "apple",
      plan: selected,
    });
    try {
      const c = await recordConsent({
        data: { productId: selected, accepted: true, version: DIGITAL_CONSENT_VERSION },
      });
      if (!c.ok) throw new Error(c.error);
      trackFunnel("checkout_opened", {
        source: "app_membership",
        provider: "apple",
        plan: selected,
      });
      const r = await RTStoreKit.purchase({ productId: selected, appAccountToken: user.id });
      if (r.status === "purchased" && r.transactionId) {
        trackFunnel("payment_completed", {
          source: "app_membership",
          provider: "apple",
          plan: selected,
          dedupeKey: r.transactionId,
        });
        await finish([r.transactionId]);
      }
      else if (r.status === "pending")
        setMessage({ tone: "ok", text: "Your purchase is waiting for approval. We'll unlock access as soon as Apple confirms." });
    } catch (e) {
      const reason = e instanceof Error ? e.message : "Purchase failed.";
      setMessage({ tone: "err", text: reason });
      trackCheckoutFailed({
        source: "app_membership",
        provider: "apple",
        plan: selected,
        reason,
        stage: "provider",
        fallback: "/app-membership",
      });
    } finally {
      setBusy(null);
    }
  };

  const restore = async () => {
    if (!native || !user) return;
    setBusy("restore");
    setMessage(null);
    try {
      const r = await RTStoreKit.restore();
      if (!r.transactionIds.length) {
        setMessage({ tone: "err", text: "No Apple purchases were found for this Apple ID." });
      } else await finish(r.transactionIds);
    } catch (e) {
      setMessage({ tone: "err", text: e instanceof Error ? e.message : "Restore failed." });
    } finally {
      setBusy(null);
    }
  };

  if (!ready || (!native && !preview)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const alreadyMember = eligibility?.isMember;
  const appleMember = alreadyMember && eligibility?.provider === "apple";

  return (
    <div data-iap className="mx-auto max-w-lg px-4 pb-16 pt-8">
      <div className="text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
          <Crown className="h-6 w-6 text-foreground" aria-hidden="true" />
        </div>
        <h1 className="mt-4 font-display text-3xl text-foreground">Recovery Trauma™ Membership</h1>
        <p className="mt-2 text-sm text-foreground/75">
          Full access to every tool, at your own pace. Cancel anytime.
        </p>
      </div>

      <ul className="mt-6 space-y-2 rounded-2xl border border-gold/30 bg-card/80 p-4">
        {INCLUDED.map((t) => (
          <li key={t} className="flex items-start gap-2 text-sm text-foreground/85">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" aria-hidden="true" />
            {t}
          </li>
        ))}
      </ul>

      {alreadyMember ? (
        <div className="mt-6 rounded-2xl border border-gold/40 bg-card p-5 text-center">
          <p className="font-medium text-foreground">You're already a member.</p>
          <p className="mt-1 text-sm text-foreground/70">
            {appleMember
              ? "Your membership is billed through Apple."
              : "Your membership was set up on the Recovery Trauma™ website, so there's nothing more to buy."}
          </p>
          {appleMember && (
            <Button variant="outline" className="mt-4" onClick={() => void RTStoreKit.manageSubscriptions()}>
              Manage subscription
            </Button>
          )}
        </div>
      ) : (
        <>
          <div role="radiogroup" aria-label="Membership plans" className="mt-6 space-y-3">
            {plans.map((p) => {
              const on = selected === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setSelected(p.id)}
                  className={`flex w-full items-center justify-between rounded-2xl border-2 px-4 py-4 text-start transition ${
                    on ? "border-[var(--gold-deep)] bg-card shadow-[var(--shadow-gold)]" : "border-gold/30 bg-card/70"
                  }`}
                >
                  <span>
                    <span className="block font-semibold text-foreground">{p.name}</span>
                    {p.note && <span className="block text-xs text-[var(--gold-deep)]">{p.note}</span>}
                  </span>
                  <span className="text-end">
                    <span className="block text-lg font-semibold text-foreground">{p.price}</span>
                    <span className="block text-xs text-foreground/65">{p.per}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {user || preview ? (
            <>
              <div className="mt-6">
                <DigitalConsentCheckbox checked={consent} onChange={setConsent} id="apple-digital-consent" />
              </div>
              <Button
                size="lg"
                className="mt-4 w-full"
                disabled={!!busy || !consent || (!native && !preview) || (native && !eligibility)}
                onClick={() => void buy()}
              >
                {busy === "buy" ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
                Continue
              </Button>
            </>
          ) : (
            <div className="mt-6 space-y-3 text-center">
              <Button asChild size="lg" className="w-full">
                <Link to="/signup" search={{ apple: selected } as never}>
                  Continue with {plans.find((p) => p.id === selected)?.name} — create account
                </Link>
              </Button>
              <p className="text-xs text-foreground/65">
                Next you'll create your account, then confirm payment securely with Apple. Your journals and
                progress stay with your account.
              </p>
              <Link
                to="/login"
                search={{ redirect: "/app-membership" } as never}
                className="inline-flex min-h-[44px] items-center text-sm font-medium text-[var(--gold-deep)] underline underline-offset-4"
              >
                Already a member? Log in
              </Link>
            </div>
          )}
        </>
      )}

      {message && (
        <p
          role="status"
          className={`mt-4 rounded-xl px-4 py-3 text-sm ${
            message.tone === "ok" ? "bg-card text-foreground" : "bg-destructive/10 text-destructive"
          }`}
        >
          {message.text}
        </p>
      )}

      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={() => void restore()}
          disabled={!!busy || !native || !user}
          className="inline-flex items-center gap-2 text-sm font-medium text-[var(--gold-deep)] underline-offset-4 hover:underline disabled:opacity-60"
        >
          {busy === "restore" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
          Restore Purchases
        </button>
      </div>

      <p className="mt-6 text-center text-xs leading-relaxed text-foreground/60">
        Payment is charged to your Apple ID at confirmation. Subscriptions renew automatically at the same price
        unless cancelled at least 24 hours before the end of the current period. Manage or cancel anytime in your
        Apple ID settings. Already a member from the website? Just sign in — no need to buy again.
      </p>
      <p className="mt-3 text-center text-xs">
        <Link to="/terms" className="text-[var(--gold-deep)] underline">Terms of Use</Link>
        {" · "}
        <Link to="/privacy" className="text-[var(--gold-deep)] underline">Privacy Policy</Link>
      </p>
    </div>
  );
}
